import type { Content } from "pdfmake/interfaces.js";
import { OFFICIAL_SUBTITLE, type DocumentVisual } from "../../../domain/studio/visual-design.js";

/**
 * Cabeçalho documental compartilhado dos relatórios de inspeção.
 *
 * Extraído do Relatório ME exatamente como aprovado — logo à esquerda com o
 * título em duas linhas, QR funcional ao centro, bloco documental à direita,
 * a linha UNIDADE/ÁREA + TAG/EQUIPAMENTO abaixo e a régua inferior. O ME
 * continua produzindo a mesma definição de antes; o LP passa a usar o mesmo
 * cabeçalho em vez de manter um modelo próprio.
 */

/** A4 (595.28pt) menos as margens laterais de 40pt. */
export const HEADER_USABLE_WIDTH = 515;

/** Margem superior que acomoda este cabeçalho (o ME já usava 118). */
export const HEADER_PAGE_MARGIN_TOP = 118;

/** Estilos que o cabeçalho referencia — cada builder os inclui nos seus `styles`. */
export const INSPECTION_REPORT_HEADER_STYLES = {
  headerLogo: { fontSize: 14, bold: true, color: "#002B49" },
  headerDocTitle: { fontSize: 12.5, bold: true, color: "#002B49", lineHeight: 1.12, margin: [0, 3, 0, 0] as [number, number, number, number] },
  headerSub: { fontSize: 7, color: "#666666" },
  headerTagLabel: { fontSize: 6.5, color: "#666666" },
  headerField: { fontSize: 7, color: "#333333" }
};

export interface InspectionReportHeaderOptions {
  /** "RELATÓRIO DE MEDIÇÃO DE ESPESSURA\nPOR ULTRASSOM" — a quebra explícita mantém o título em duas linhas. */
  title: string;
  /**
   * Linha de identidade sob o logo ("Sistema de Inspeção e Integridade
   * Mecânica"). Opcional: o ME aprovado não a imprime; o RI e o LP imprimem.
   */
  subtitle?: string;
  /** Conteúdo do QR — identifica o documento; gerado pelo pdfmake, nunca uma imagem fixa. */
  qrReference: string;
  /** Linhas do bloco documental à direita, na ordem em que aparecem ("Rel. cliente nº", ...). */
  fields: Array<{ label: string; value: string }>;
  unitArea: string;
  tag: string;
  /** Lado do QR em pt. Padrão 54 (o do ME); o LP pede um QR maior. Proporção sempre 1:1. */
  qrSize?: number;
  /** Largura da coluna documental à direita. Padrão 172 (o do ME); fontes maiores pedem um pouco mais. */
  fieldsWidth?: number;
  /** Largura útil da página (régua inferior). Padrão 515 (A4 retrato); a AVR, em paisagem, usa 762. */
  usableWidth?: number;
  /**
   * STUDIO INSPECT IA — o visual congelado no modelo. Ausente ou padrão =
   * exatamente o cabeçalho de sempre (documentos anteriores ao Studio saem
   * idênticos). Só muda a APARÊNCIA: nenhum campo nem dado é acrescentado ou
   * retirado.
   */
  visual?: DocumentVisual | null;
}

export function buildInspectionReportHeader(options: InspectionReportHeaderOptions): Content {
  const visual = options.visual ?? null;
  const color = visual?.colors.headerText ?? null;
  const tint = <T extends object>(node: T): T => (color ? { ...node, color } : node);
  // Logo documental: imagem publicada no Studio (PNG/JPEG) ou o texto INSPECT IA de sempre.
  const logo: Content = visual?.logoImage
    ? ({ image: visual.logoImage, fit: [150, 30], margin: [0, 0, 0, 2] } as Content)
    : tint({ text: visual?.logoText || "INSPECT IA", style: "headerLogo" });
  const leftStack: Content[] = [logo];
  if (options.subtitle) {
    // O subtítulo só troca quando foi PERSONALIZADO no Studio; o padrão
    // mantém o texto (e a caixa) que cada relatório já imprimia.
    const custom = visual && visual.subtitle.trim() && visual.subtitle.trim().toUpperCase() !== OFFICIAL_SUBTITLE ? visual.subtitle.trim() : null;
    leftStack.push({ text: custom ?? options.subtitle, style: "headerSub" });
  }
  // Quebra explícita: o título deve ler em exatamente duas linhas.
  leftStack.push(tint({ text: options.title, style: "headerDocTitle" }));

  const titleColumn = { width: "*", stack: leftStack } as Content;
  const qrColumn = buildHeaderQrCode(options.qrReference, options.qrSize);
  const fieldsColumn: Content = {
    // Mais estreita que a coluna do título: os campos são curtos e o
    // título ampliado em duas linhas precisa do espaço para não abrir
    // uma terceira.
    width: options.fieldsWidth ?? 172,
    fontSize: 7,
    stack: options.fields.map(field => ({ text: `${field.label}: ${field.value || "-"}`, style: "headerField" }))
  } as Content;
  // Posição do QR (Studio): CENTRO é o padrão aprovado; DIREITA leva o QR para o canto superior direito.
  const columns = visual?.qrPosition === "DIREITA" ? [titleColumn, fieldsColumn, qrColumn] : [titleColumn, qrColumn, fieldsColumn];

  return {
    margin: [40, 18, 40, 0],
    stack: [
      {
        columns
      },
      {
        margin: [0, 4, 0, 0],
        columns: [
          { text: `UNIDADE / ÁREA: ${options.unitArea || "-"}`, style: "headerSub" },
          { text: `TAG / EQUIPAMENTO: ${options.tag || "-"}`, style: "headerSub", alignment: "right" }
        ]
      },
      { canvas: [{ type: "line", x1: 0, y1: 4, x2: options.usableWidth ?? HEADER_USABLE_WIDTH, y2: 4, lineWidth: 0.5, lineColor: "#CCCCCC" }] }
    ]
  };
}

/** QR funcional entre as duas colunas, como no modelo oficial. Se algo falhar nele, o cabeçalho continua imprimindo. */
export function buildHeaderQrCode(reference: string, size: number = 54): Content {
  return {
    width: size + 4,
    stack: [
      { qr: reference, fit: size, foreground: "#002B49" },
      { text: "Verificação", style: "headerTagLabel", alignment: "center" }
    ]
  } as Content;
}
